/**
 * Host-level Vue app install entrypoint.
 *
 * `installApp(app)` provides the host-owned ports the feature modules
 * assume are already in place by the time `installGvgPlanner(...)`
 * runs:
 *
 *   1. The Apollo client is provided twice — once under the official
 *      `DefaultApolloClient` key (so `@vue/apollo-composable`'s
 *      `useQuery` / `useMutation` / `useSubscription` resolve it inside
 *      any component's setup) and once under the project's
 *      `APOLLO_PORT` injection key (so feature stores read through the
 *      same client via `useApollo()`). Both `provide`s point at the
 *      same `apolloClient` instance.
 *
 *   2. The host-owned `CONFIRM_PORT` adapter — the project-wide seam
 *      for `window.confirm`. Phase 3 keeps the browser API call out of
 *      feature stores; this is the only place it gets bridged.
 *
 *   3. The session port (`provideSession(app)`) and the guild port
 *      (`provideGuild(app)`). Both are app-level singletons the planner
 *      store reads through `useSession()` / `useGuild()`
 *      (`@/shared/ports`).
 *
 * The router is NOT registered on the app here — `app.use(router)` is
 * called by `main.ts` AFTER `installGvgPlanner(...)` has added every
 * feature route. `app.use(router)` runs the router's `install(app)`
 * which synchronously fires the initial navigation via
 * `push(routerHistory.location)`; if it runs before the feature routes
 * are in the matcher, the initial `'/'` resolves to the host's redirect
 * `{ name: 'calendar' }` and `'calendar'` is not yet registered — a
 * `MATCHER_NOT_FOUND` failure (`Uncaught Error: No match for
 * {"name":"calendar",…}`) surfaces through Vue Router's devtools
 * overlay. Wiring `app.use(router)` after `installGvgPlanner(...)`
 * guarantees the matcher contains every route the initial nav may
 * resolve to.
 *
 * The function is `async` so future ports (e.g. boot-time REST probes
 * that aren't already covered by `fetchSession()` in `main.ts`) can be
 * added inside without breaking the call site shape.
 */
import type { App } from 'vue'

import { DefaultApolloClient } from '@vue/apollo-composable'

import { APOLLO_PORT, CONFIRM_PORT } from '@/shared/ports'
import { apolloClient } from '@/shared/api/apolloClient'
import { provideSession } from '@/app/session'
import { provideGuild } from '@/app/guild'

export async function installApp(app: App): Promise<void> {
  app.provide(DefaultApolloClient, apolloClient)
  app.provide(APOLLO_PORT, apolloClient)
  app.provide(CONFIRM_PORT, {
    confirm: (message: string) => window.confirm(message),
  })

  provideSession(app)
  provideGuild(app)
}
